import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import type { Transport } from "@tdelivr/api-client";
import { services } from "@tdelivr/api-client";
// The row shape GEO-06 publishes, imported rather than restated. A column here that the service
// stopped returning is a build failure, not a blank cell.
import type { ServiceArea } from "@tdelivr/api-client/generated/geo/service-area.js";

import {
  boundedNote,
  coverageText,
  servingNow,
  stateOf,
  stateReason,
  zonePage,
  FILTERABLE_LIFECYCLES,
  PER_FETCH,
  type AreaLifecycle,
  type ZoneOrder,
} from "../zones.ts";
import { stateForError, stateForList, type ViewState } from "../view-state.ts";
import { PageHeading } from "./PageHeading.tsx";
import { StateBadge } from "./StateBadge.tsx";
import { StateView } from "./StateView.tsx";

/**
 * Zones and coverage: the service areas this tenant has configured, strongest precedence first.
 *
 * Built to `17 WP16 Tenant Admin.dc.html`, page `zones`, against GEO-06 `GET /v1/service-areas` —
 * which is the operation `SCREEN_SPEC.md` does not list for this page, because it was published
 * after the design bundle was cut. The page body's measurements are the same ones the tenant audit
 * board uses, because the two are the same template in the same file.
 *
 * **Three of the design's three columns have a source and none of its three state words does.**
 * `Zone` is `name`, `Region` is `marketRef`, `Coverage` is `productRefs`. The badges read `served`,
 * `partial` and `review request`; GEO-06 publishes `lifecycle: draft | active | inactive`, which
 * answers whether the *record* is in force rather than how much service the area gets — and
 * `partial`, which is this page's stated reason for existing, has no field anywhere on the row. So
 * the words on the badges are the contract's own and R-13 in
 * `docs/design/DESIGN_RECONCILIATION.md` is the measurement.
 *
 * **An area stored `active` is not necessarily serving anybody, and this board does not say it is.**
 * GEO-06's own availability evaluation — the operation that decides whether a customer standing
 * somewhere can book — selects on `lifecycle='active' AND effective_from<=$at AND (effective_until
 * IS NULL OR effective_until>$at)`. An `active` area outside that window is not a candidate: the
 * platform will not offer it and a customer there gets nothing, while its row still reads `active`.
 * Both correcting fields are published on every row, so the screen is simply right. `servingNow()`
 * in `zones.ts` holds the rule with the query beside it.
 */

const areasApi = services.geo__service_area;

/** The design's own rows-per-page choices, in its order. */
const PER_PAGE_CHOICES = [5, 10, 25, 50] as const;

/** The design's sort options, in its order and its words. */
const ORDER_CHOICES: readonly { readonly value: ZoneOrder; readonly label: string }[] = [
  { value: "default", label: "Default order" },
  { value: "state", label: "State" },
  { value: "az", label: "Name A–Z" },
  { value: "za", label: "Name Z–A" },
];

/** The export formats the design's menu offers, in its order and its words. */
const EXPORTS = [
  { format: "csv", glyph: "description", label: "CSV" },
  { format: "xls", glyph: "table_view", label: "Excel (.xls)" },
  { format: "pdf", glyph: "picture_as_pdf", label: "PDF" },
] as const;

type ExportFormat = (typeof EXPORTS)[number]["format"];

export interface ZonesCoverageProps {
  readonly transport: Transport;
  /** Innermost last: tenant, group, page. Rendered in the page body, never in the top bar. */
  readonly crumbs?: readonly string[];
  /** Injected so the effective-window rule can be exercised without waiting for a date to pass. */
  readonly now?: () => number;
}

interface Loaded {
  readonly rows: readonly ServiceArea[];
  /** GEO-06 answered a cursor, so this is the head of the list rather than all of it. */
  readonly truncated: boolean;
}

export function ZonesCoverage({ transport, crumbs, now }: ZonesCoverageProps): ReactNode {
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [search, setSearch] = useState("");
  const [lifecycle, setLifecycle] = useState<AreaLifecycle | undefined>(undefined);
  const [order, setOrder] = useState<ZoneOrder>("default");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [exportOpen, setExportOpen] = useState(false);
  const exportRegion = useRef<HTMLSpanElement>(null);

  const load = useCallback(async () => {
    const client = areasApi.createServiceAreaClient(transport);
    try {
      const answered = await client.getServiceAreas(lifecycle, PER_FETCH, undefined);
      setLoaded({
        rows: answered.areas,
        truncated: typeof answered.nextAfter === "string" && answered.nextAfter.length > 0,
      });
      setFailure(undefined);
    } catch (error) {
      setFailure(stateForError(error, ["service-area:read"]));
      setLoaded(undefined);
    }
  }, [transport, lifecycle]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!exportOpen) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setExportOpen(false);
    };
    const onPointer = (event: MouseEvent): void => {
      const region = exportRegion.current;
      if (region !== null && !region.contains(event.target as Node)) setExportOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return (): void => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [exportOpen]);

  const instant = now === undefined ? Date.now() : now();

  const view = useMemo(
    () => zonePage(loaded?.rows ?? [], { search, order, page, perPage, now: instant }),
    [loaded, search, order, page, perPage, instant],
  );

  /*
   * No `observedAt`, so no stale state.
   *
   * GEO-06's listing publishes `updatedAt` per row — when the area was last changed — and no
   * page-level instant. A configuration board is not a live board, so this is the least costly of
   * the four places this absence has now been found; it is recorded in R-13 all the same, because
   * the recommendation is the same one and it is one field.
   */
  const viewState = failure ?? stateForList(loaded?.rows, { filtered: lifecycle !== undefined });

  const clearFilters = (): void => {
    setSearch("");
    setLifecycle(undefined);
    setPage(1);
  };

  const bounded = boundedNote(
    loaded?.rows.length,
    loaded?.truncated ?? false,
    lifecycle,
    Number(PER_FETCH),
  );

  /*
   * The heading row, hoisted because the loading state draws it too.
   *
   * In `02 Console Shell v3` the headings sit outside the state switch: the design shimmers the
   * rows beneath a heading row that never moves, so the board does not jump when the data lands.
   * One element rather than two copies of this grid, which would be free to disagree. Q-287.
   */
  const head = (
    <div className="records__head">
      <span>Zone</span>
      <span>Region</span>
      <span>Coverage</span>
      <span className="records__head--right">State</span>
    </div>
  );

  return (
    <section className="page records">
      <PageHeading
        crumbs={crumbs ?? []}
        title="Zones and coverage"
        source="GEO-06"
        description="Service areas, partial coverage and the reason shown to customers outside them."
        action={
          <button
            type="button"
            className="records__primary"
            /*
             * Drawn, and refused. The design's form has seven fields and three of them have no
             * field on the wire to write to: `Coverage` chooses between Served, Partial and Not
             * served, `Hours (if partial)` restricts the times, and `Reason shown to customers` is
             * the sentence somebody outside the area reads. GEO-06's `Definition` publishes `name`,
             * `marketRef`, `productRefs`, `priority`, `lifecycle`, `effectiveFrom`,
             * `effectiveUntil`, `boundary` and `expectedVersion` — and none of those three.
             *
             * Creating is also not a create: the only write is `PUT /v1/service-areas/{id}`, which
             * needs an identifier and an `expectedVersion` the form would have to invent. R-13.
             */
            disabled
            title="GEO-06 publishes no field for coverage, hours or the reason shown to customers, and its only write is a PUT to an existing identifier. See R-13."
          >
            <span aria-hidden="true" className="records__primary-glyph">
              add
            </span>
            Add zone
          </button>
        }
      />

      {/*
        No tiles, where the design has four.

        `Zones · 34`, `Partial · 5`, `Not served · 3` and `Requests · 18` are three counts over a
        coverage vocabulary the contract does not have and one count of customer requests for new
        areas, which nothing on the platform records. The first could be a count of the rows this
        page holds, and R-04's measurement is why it is not one: no listing publishes a total, so a
        tile reading 34 would mean "34 so far" under a label that means "34". R-13.
      */}

      <div className="records__panel">
        <div className="records__toolbar">
          <span className="records__search">
            <span aria-hidden="true" className="records__search-glyph">
              search
            </span>
            <input
              id="zones-search"
              type="search"
              aria-label="Search"
              placeholder="Search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </span>
          <select
            id="zones-lifecycle"
            aria-label="Filter by state"
            value={lifecycle ?? ""}
            onChange={(event) => {
              setLifecycle(
                event.target.value === "" ? undefined : (event.target.value as AreaLifecycle),
              );
              setPage(1);
            }}
          >
            {/*
              The design's word for the unfiltered option, over GEO-06's own three lifecycles rather
              than over the states on screen. Choosing one refetches: this listing publishes a
              `lifecycle` query parameter, so a filter built from the rows held would narrow a page
              the service could have narrowed properly.

              The options are the lifecycles and not the badges' words, because the lifecycles are
              what the service accepts. An `active` zone outside its effective window appears under
              `active` and reads `no longer effective`, which is the truth about it rather than a
              fourth option the service would refuse.
            */}
            <option value="">All states</option>
            {FILTERABLE_LIFECYCLES.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
          <select
            id="zones-order"
            aria-label="Sort"
            value={order}
            onChange={(event) => {
              setOrder(event.target.value as ZoneOrder);
            }}
          >
            {ORDER_CHOICES.map((choice) => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </select>
          <span className="records__export" ref={exportRegion}>
            <button
              type="button"
              className="records__export-button"
              aria-expanded={exportOpen}
              onClick={() => {
                setExportOpen((open) => !open);
              }}
            >
              <span aria-hidden="true" className="records__export-glyph">
                download
              </span>
              Export
            </button>
            {exportOpen ? (
              <div className="records__export-menu" role="menu">
                {EXPORTS.map((choice) => (
                  <button
                    key={choice.format}
                    type="button"
                    role="menuitem"
                    className="records__export-item"
                    onClick={() => {
                      setExportOpen(false);
                      exportRows(view.all, choice.format, instant);
                    }}
                  >
                    <span aria-hidden="true" className="records__export-item-glyph">
                      {choice.glyph}
                    </span>
                    {choice.label}
                  </button>
                ))}
              </div>
            ) : null}
          </span>
        </div>

        <StateView
          state={viewState}
          purpose="Zones appear here as soon as one is configured for this tenant."
          onRetry={() => {
            void load();
          }}
          skeleton={{
            head,
            columns: 4,
            rowClassName: "records__row",
            // Both strings come from the trail the shell already draws, so the
            // sentence can never disagree with the breadcrumb above it.
            page: crumbs?.at(-1) ?? "this page",
            tenant: crumbs?.[0] ?? "this tenant",
          }}
        >
          {head}
          <div className="records__rows">
            {view.rows.map((area) => {
              const shown = stateOf(area, instant);
              const reason = stateReason(area, instant);
              return (
                <div
                  key={area.id}
                  className={
                    area.lifecycle === "active" && !servingNow(area, instant)
                      ? "records__row records__row--cross"
                      : "records__row"
                  }
                >
                  <span className="records__when">{area.name}</span>
                  <span className="records__actor">{area.marketRef}</span>
                  <span className="records__action">{coverageText(area)}</span>
                  <span className="records__state">
                    <StateBadge
                      code="GEO-06"
                      status={area.lifecycle}
                      classKey={shown.classKey}
                      label={shown.label}
                      {...(reason === undefined ? {} : { title: reason })}
                    />
                  </span>
                </div>
              );
            })}
          </div>
          {view.total === 0 ? (
            <div className="records__no-match">
              <span aria-hidden="true" className="records__no-match-glyph">
                search_off
              </span>
              Nothing matches this search or filter.
              <button type="button" className="records__clear" onClick={clearFilters}>
                Clear filters
              </button>
            </div>
          ) : null}
          <div className="records__pager">
            <span>
              {view.total === 0
                ? "No matching rows"
                : `Showing ${(page - 1) * perPage + 1}–${Math.min(page * perPage, view.total)} of ${view.total}`}
            </span>
            <span className="records__pager-controls">
              <select
                id="zones-per-page"
                aria-label="Rows per page"
                value={perPage}
                onChange={(event) => {
                  setPerPage(Number(event.target.value));
                  setPage(1);
                }}
              >
                {PER_PAGE_CHOICES.map((choice) => (
                  <option key={choice} value={choice}>
                    {choice} per page
                  </option>
                ))}
              </select>
              <button
                type="button"
                aria-label="Previous page"
                disabled={page <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                <span aria-hidden="true">chevron_left</span>
              </button>
              <span className="records__page">
                Page {Math.min(page, view.pageCount)} of {view.pageCount}
              </span>
              <button
                type="button"
                aria-label="Next page"
                disabled={page >= view.pageCount}
                onClick={() => setPage((current) => Math.min(view.pageCount, current + 1))}
              >
                <span aria-hidden="true">chevron_right</span>
              </button>
            </span>
          </div>
        </StateView>

        {bounded === undefined ? null : (
          <p className="records__bounded" role="status">
            {bounded}
          </p>
        )}
      </div>

      {/*
        The closing callout, in the design's own `warn` style — and this is the one place on either
        screen where the design's words are not kept.

        The design writes "Coverage read responses are not published yet (G-16); the website check
        uses the same data." Two problems, and the second is the reason: the sentence is false,
        because G-16 is closed and GEO-06's response schemas are published — which is how this screen
        exists at all — and it is an annotation to the design team about a contract gap, addressed to
        a tenant administrator who has no idea what G-16 is. Shipping an internal gap reference to a
        customer is wrong whether or not it is true.

        So the callout keeps its style, its glyph and its place, and carries the measured fact its
        sentence was pointing at: what GEO-06 does not publish is partial coverage. Written here
        rather than read from the design, and recorded as Q-22 for that reason.
      */}
      <div className="records__note records__note--warn">
        <span aria-hidden="true" className="records__note-glyph">
          warning
        </span>
        <span>
          GEO-06 publishes which products a zone is configured for and no restricted hours, so a row
          cannot say that coverage is partial.
        </span>
      </div>
    </section>
  );
}

/**
 * Export what the administrator is looking at, in the columns they are looking at.
 *
 * The filtered and ordered set rather than the page, and the words on screen rather than the raw
 * lifecycle — including the effective-window correction, so an exported row says the same thing the
 * board did.
 */
function exportRows(rows: readonly ServiceArea[], format: ExportFormat, now: number): void {
  const header = ["Zone", "Region", "Coverage", "State"];
  const body = rows.map((area) => [
    area.name,
    area.marketRef,
    coverageText(area),
    stateOf(area, now).label,
  ]);

  if (format === "csv") {
    const quote = (value: string): string => `"${value.replaceAll('"', '""')}"`;
    const csv = [header, ...body].map((line) => line.map(quote).join(",")).join("\n");
    download(new Blob([csv], { type: "text/csv" }), "zones.csv");
    return;
  }

  const table =
    '<table border="1" cellpadding="6" style="border-collapse:collapse;font-family:sans-serif;font-size:12px">' +
    `<tr>${header.map((cell) => `<th>${escapeHtml(cell)}</th>`).join("")}</tr>` +
    body
      .map((line) => `<tr>${line.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`)
      .join("") +
    "</table>";

  if (format === "xls") {
    download(
      new Blob([`<html><head><meta charset="utf-8"></head><body>${table}</body></html>`], {
        type: "application/vnd.ms-excel",
      }),
      "zones.xls",
    );
    return;
  }

  const printable = window.open("", "_blank");
  if (printable === null) return;
  printable.document.write(
    `<html><head><title>zones</title></head><body><h2 style="font-family:sans-serif">zones</h2>${table}</body></html>`,
  );
  printable.document.close();
  printable.focus();
  printable.print();
}

function escapeHtml(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
}

function download(blob: Blob, name: string): void {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = name;
  document.body.append(link);
  link.click();
  setTimeout(() => {
    URL.revokeObjectURL(link.href);
    link.remove();
  }, 500);
}
